import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchApiWithToken } from '~/api/utils'
import type {
  AdminConfigResponse,
  ConfigKeyMap,
  ConfigName,
  ConfigResponse,
  DomainKeys
} from '../types'

const configBatcher = (() => {
  const names = new Set<string>()
  const resolvers = new Map<string, (value: ConfigResponse['value']) => void>()
  const rejectors = new Map<string, (error: Error) => void>()
  let batchTimeout: ReturnType<typeof setTimeout> | null = null

  const executeBatch = async () => {
    const namesToFetch = Array.from(names)
    names.clear()
    const currentResolvers = new Map(resolvers)
    const currentRejectors = new Map(rejectors)
    resolvers.clear()
    rejectors.clear()
    batchTimeout = null

    if (namesToFetch.length === 0) {
      return
    }

    const searchParams = new URLSearchParams()
    for (const name of namesToFetch) {
      searchParams.append('name[]', name)
    }

    try {
      const configs = await fetchApiWithToken<{
        [key: string]: ConfigResponse
      }>({
        method: 'get',
        url: `/v1/configuration?${searchParams.toString()}`
      })

      namesToFetch.forEach((name) => {
        const resolver = currentResolvers.get(name)
        if (resolver && configs[name]) {
          resolver(
            Array.isArray(configs[name]) ? configs[name] : configs[name].value
          )
        } else {
          const rejector = currentRejectors.get(name)
          if (rejector) {
            rejector(new Error(`Config ${name} not found`))
          }
        }
      })
    } catch (error) {
      currentRejectors.forEach((rejector) => {
        rejector(error as Error)
      })
    }
  }

  return {
    fetch: (name: string): Promise<ConfigResponse['value']> => {
      names.add(name)

      const promise = new Promise<ConfigResponse['value']>(
        (resolve, reject) => {
          resolvers.set(name, resolve)
          rejectors.set(name, reject)
        }
      )

      if (batchTimeout) {
        clearTimeout(batchTimeout)
      }
      batchTimeout = setTimeout(executeBatch, 10)

      return promise
    }
  }
})()

const fetchAdminConfig = async (
  name: string
): Promise<AdminConfigResponse[]> => {
  return fetchApiWithToken<AdminConfigResponse[]>({
    method: 'get',
    url: `/v1/configuration/admin?name=${encodeURIComponent(name)}`
  })
}

const fetchAgencyConfig = async (
  name: string,
  agencyId: number
): Promise<ConfigResponse['value']> => {
  const result = await fetchApiWithToken<{ [key: string]: ConfigResponse }>({
    method: 'get',
    url: `/v1/configuration?name[]=${encodeURIComponent(name)}&agency=/v1/agencies/${agencyId}`
  })
  const entry = result[name]
  return entry ? (Array.isArray(entry) ? entry : entry.value) : null
}

export const useAdminConfig = (
  name: string,
  options?: { refetchInterval?: number; enabled?: boolean }
) => {
  return useQuery<AdminConfigResponse[], Error>({
    queryKey: ['adminConfig', name],
    queryFn: () => fetchAdminConfig(name),
    staleTime: 1000 * 60 * 60 * 24,
    refetchInterval: options?.refetchInterval ?? false,
    enabled: options?.enabled ?? true
  })
}

export const useConfigDomain = <D extends string>(domain: D) => {
  const queryClient = useQueryClient()
  return useQuery<DomainKeys<D>, Error>({
    queryKey: ['config', domain],
    queryFn: async () => {
      const searchParams = new URLSearchParams()
      searchParams.append('name[]', domain)
      const result = await fetchApiWithToken<{ [key: string]: ConfigResponse }>(
        {
          method: 'get',
          url: `/v1/configuration?${searchParams.toString()}`
        }
      )
      const stripped: Record<string, unknown> = {}
      for (const [key, entry] of Object.entries(result)) {
        const value = Array.isArray(entry) ? entry : entry.value
        queryClient.setQueryData(['config', key], value)
        const subKey = key.startsWith(`${domain}.`)
          ? key.slice(domain.length + 1)
          : key
        stripped[subKey] = value
      }
      return stripped as DomainKeys<D>
    },
    staleTime: 1000 * 60 * 60 * 24
  })
}

/**
 * Cache entry of a single configuration key, shared by `useConfig` and by any caller that needs a
 * dynamic list of keys through `useQueries`. Reads go through the batcher, so keys requested in the
 * same tick travel in one request.
 */
export const configQueryOptions = <K extends ConfigName>(name: K) => ({
  queryKey: ['config', name],
  queryFn: () => configBatcher.fetch(name) as Promise<ConfigKeyMap[K]>,
  staleTime: 1000 * 60 * 60 * 24
})

export const useConfig = <K extends ConfigName>(
  name?: K,
  {
    refetchInterval,
    enabled = true
  }: {
    refetchInterval?: number | undefined
    enabled?: boolean
  } = {}
) =>
  useQuery<ConfigKeyMap[K], Error>({
    ...configQueryOptions(name as K),
    refetchInterval: refetchInterval ?? false,
    enabled: enabled && name !== undefined
  })

export const useAgencyConfig = <K extends ConfigName>(
  name: K,
  agencyId: number | undefined,
  {
    refetchInterval
  }: {
    refetchInterval?: number | undefined
  } = {}
) =>
  useQuery<ConfigKeyMap[K] | undefined, Error>({
    queryKey: ['config', name, agencyId],
    queryFn: async () => {
      if (agencyId === undefined) return undefined
      const value = await fetchAgencyConfig(name as string, agencyId)
      return (value ?? undefined) as ConfigKeyMap[K] | undefined
    },
    enabled: agencyId !== undefined,
    staleTime: 1000 * 60 * 60 * 24,
    refetchInterval: refetchInterval ?? false
  })
